import { useEffect, useRef, useState } from 'react';
import { create } from 'zustand';
import { AlertTriangle, Info, Pencil } from 'lucide-react';

// ---------------------------------------------------------------------------
// Globales, promise-basiertes Dialog-System · ersetzt native confirm/alert/prompt.
// Aufruf von ueberall: `if (await confirmDialog('...')) { ... }`.
// Ein einziger <DialogHost/> (in der AppShell gemountet) rendert die Overlays.
// Mehrere gleichzeitige Aufrufe werden serialisiert (Queue), nichts geht verloren.
// ---------------------------------------------------------------------------

type DialogKind = 'confirm' | 'alert' | 'prompt';
type Tone = 'default' | 'danger';

interface BaseOptions {
  title?: string;
  message: string;
  tone?: Tone;
}
export interface ConfirmOptions extends BaseOptions {
  confirmLabel?: string;
  cancelLabel?: string;
}
export interface AlertOptions extends BaseOptions {
  confirmLabel?: string;
}
export interface PromptOptions extends BaseOptions {
  confirmLabel?: string;
  cancelLabel?: string;
  defaultValue?: string;
  placeholder?: string;
}

interface DialogRequest extends BaseOptions {
  id: number;
  kind: DialogKind;
  confirmLabel?: string;
  cancelLabel?: string;
  defaultValue?: string;
  placeholder?: string;
  resolve: (value: boolean | string | null) => void;
}

interface DialogStore {
  current: DialogRequest | null;
  queue: DialogRequest[];
  enqueue: (req: DialogRequest) => void;
  close: (result: boolean | string | null) => void;
}

const useDialogStore = create<DialogStore>((set, get) => ({
  current: null,
  queue: [],
  enqueue: (req) => {
    if (get().current) set((s) => ({ queue: [...s.queue, req] }));
    else set({ current: req });
  },
  close: (result) => {
    const cur = get().current;
    if (cur) cur.resolve(result);
    set((s) => {
      const [next, ...rest] = s.queue;
      return { current: next ?? null, queue: rest };
    });
  },
}));

let idc = 0;
function ask(req: Omit<DialogRequest, 'id' | 'resolve'>): Promise<boolean | string | null> {
  return new Promise((resolve) => {
    useDialogStore.getState().enqueue({ ...req, id: ++idc, resolve });
  });
}

/** Designter Ersatz fuer window.confirm — resolved true/false. */
export function confirmDialog(opts: string | ConfirmOptions): Promise<boolean> {
  const o = typeof opts === 'string' ? { message: opts } : opts;
  return ask({ kind: 'confirm', ...o }) as Promise<boolean>;
}

/** Designter Ersatz fuer window.alert — resolved wenn bestaetigt. */
export function alertDialog(opts: string | AlertOptions): Promise<void> {
  const o = typeof opts === 'string' ? { message: opts } : opts;
  return ask({ kind: 'alert', ...o }).then(() => undefined);
}

/** Designter Ersatz fuer window.prompt — resolved den Text oder null bei Abbruch. */
export function promptDialog(opts: string | PromptOptions): Promise<string | null> {
  const o = typeof opts === 'string' ? { message: opts } : opts;
  return ask({ kind: 'prompt', ...o }) as Promise<string | null>;
}

// ---------------------------------------------------------------------------

export function DialogHost() {
  const current = useDialogStore((s) => s.current);
  const close = useDialogStore((s) => s.close);
  const inputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState('');

  // Bei neuem Prompt: Feld mit defaultValue vorbelegen + fokussieren.
  useEffect(() => {
    if (current?.kind === 'prompt') {
      setValue(current.defaultValue ?? '');
      const id = setTimeout(() => inputRef.current?.select(), 30);
      return () => clearTimeout(id);
    }
  }, [current?.id, current?.kind, current?.defaultValue]);

  useEffect(() => {
    if (!current) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close(current.kind === 'alert' ? true : current.kind === 'prompt' ? null : false);
      } else if (e.key === 'Enter' && current.kind !== 'prompt') {
        e.preventDefault();
        close(true);
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [current, close]);

  if (!current) return null;

  const { kind, tone = 'default', title, message, placeholder } = current;
  const danger = tone === 'danger';
  const Icon = kind === 'alert' ? Info : kind === 'prompt' ? Pencil : AlertTriangle;
  const cancelLabel = current.cancelLabel ?? 'Abbrechen';
  const confirmLabel = current.confirmLabel ?? (kind === 'alert' ? 'OK' : kind === 'prompt' ? 'Speichern' : 'Bestätigen');

  const cancel = () => close(kind === 'prompt' ? null : false);
  const submit = () => close(kind === 'prompt' ? value : true);

  return (
    <div
      className="fraud0-area fraud0-light bo-purple fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-[fadeIn_0.15s_ease-out]"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) cancel();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        className="w-full max-w-sm bg-bg-surface/95 backdrop-blur-xl border border-border-primary rounded-2xl p-6 shadow-2xl ring-1 ring-white/5 animate-[popIn_0.18s_ease-out]"
      >
        <div className="flex items-start gap-3 mb-4">
          <div
            className={`shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
              danger ? 'bg-danger/15 text-danger' : 'bg-accent/15 text-accent'
            }`}
          >
            <Icon size={20} />
          </div>
          <div className="min-w-0 flex-1">
            {title && <h2 className="text-base font-bold text-text-main leading-tight">{title}</h2>}
            <p className={`text-sm text-text-muted whitespace-pre-line ${title ? 'mt-1' : 'mt-1.5'}`}>{message}</p>
          </div>
        </div>

        {kind === 'prompt' && (
          <input
            ref={inputRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                submit();
              }
            }}
            placeholder={placeholder}
            className="w-full mb-5 px-3.5 py-2.5 rounded-xl bg-bg-input border border-border-primary text-sm text-text-main placeholder:text-text-muted/60 focus:outline-none focus:border-accent transition-colors"
          />
        )}

        <div className="flex items-center gap-3">
          {kind !== 'alert' && (
            <button
              onClick={cancel}
              className="flex-1 py-2.5 rounded-xl border border-border-primary text-sm font-semibold text-text-muted hover:text-text-main hover:bg-bg-elevated transition-colors"
            >
              {cancelLabel}
            </button>
          )}
          <button
            onClick={submit}
            className={`flex-1 py-2.5 rounded-xl text-sm font-semibold text-white transition-opacity hover:opacity-90 ${
              danger ? 'bg-danger' : 'bg-accent'
            }`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
